import { createSelector } from '@reduxjs/toolkit';
import { isCacheExpired } from 'config/cache.config';
import { reduxEqualityFunction } from 'library/helpers/utility';
import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { peopleActions } from 'reduxStore/people/slice';
import { Loader } from 'zComponents/atoms';
import { Flex, Typography } from 'zui/Antd';
import ViewMoreToggle from '../ViewMoreToggle';

const { Text } = Typography;

// Selector to get person by ID
const selectPersonByID = createSelector(
	[(state) => state.People.data, (_, personID) => personID],
	(people, personID) => people.find((person) => person.personID === personID) || {}
);

// Selector to get teams from the array of teamIDs
const selectTeamsByTeamIDs = createSelector([(state) => state.Teams.data, (_, teamIDs) => teamIDs], (teams, teamIDs) =>
	teams.filter((team) => teamIDs.includes(team.teamID))
);

const PeopleTeamsPanel = ({ personID }) => {
	const dispatch = useDispatch();

	const [showAll, setShowAll] = useState(false);

	const maxItemsToShow = 6; // Limit of items to show initially

	// Get person data from the store
	const person = useSelector((state) => selectPersonByID(state, personID), {
		equalityFn: reduxEqualityFunction
	});

	// Load teams data on component mount
	useEffect(() => {
		const isCacheStale = isCacheExpired(person?.teams?.timeStamp, 1);

		// If teams data is not available or cache is stale, load teams data
		if (!person?.teams || isCacheStale) {
			dispatch(peopleActions.loadPeopleTeams({ personID }));
		}
	}, [person.teams, personID, dispatch]);

	// Get teams data from the store
	const teams = useSelector((state) => selectTeamsByTeamIDs(state, person?.teams?.data || []), {
		equalityFn: reduxEqualityFunction
	});

	// Determine the number of teams to display based on showAll state
	const visibleTeams = showAll ? teams : teams.slice(0, maxItemsToShow);

	// Get loading state from the store
	const loading = useSelector((state) => state.People.loadingTeams);
	return loading ? (
		<Loader />
	) : (
		<Fragment>
			{/* // If no teams data is available, return null */}
			{visibleTeams.length === 0 && <Text type='secondary'>No teams available.</Text>}

			<Flex wrap gap='middle'>
				{visibleTeams?.length > 0 &&
					visibleTeams.map((team) => (
						<Flex vertical key={team.teamID}>
							<Text type='secondary' style={{ lineHeight: '20px' }}>
								Team
							</Text>
							<Text strong style={{ lineHeight: '20px' }}>
								{team.name}
							</Text>
						</Flex>
					))}
			</Flex>

			{person.teams?.data.length > maxItemsToShow && (
				<ViewMoreToggle
					data={person.teams?.data}
					maxItemsToShow={maxItemsToShow}
					onToggle={(showAll) => setShowAll(showAll)}
				/>
			)}
		</Fragment>
	);
};

export default PeopleTeamsPanel;
